import { path } from 'ramda'

import { downloadFile, isAbortError } from '@common/utils'

const PERFORMANCE_DATA_LOADING = 'PERFORMANCE_DATA_LOADING'
const PERFORMANCE_DATA_LOADED = 'PERFORMANCE_DATA_LOADED'
const PERFORMANCE_DATA_FAILED = 'PERFORMANCE_DATA_FAILED'

const PERFORMANCE_RESET_STATE = 'PERFORMANCE_RESET_STATE'

const defaultState = {}

const entityName = 'performance'

const calculateTotals = (apiData) => {
  const ignoreFields = [
    'id',
    'projectedResults',
    'property',
    'propertyName',
    'startDate',
    'endDate',
  ]
  const avgFields = ['chargeRate']

  const isIgnored = (field) => ignoreFields.includes(field)
  const isAverageField = (field) => avgFields.includes(field)

  const accumulate = (acc, key, value) => {
    if (!acc[key]) acc[key] = []
    acc[key].push(Number(value))
  }

  const reduceValues = (values, key) =>
    isAverageField(key)
      ? values.reduce((sum, val) => sum + val, 0) / values.length
      : values.reduce((sum, val) => sum + val, 0)

  const totals = {}
  const projectedTotals = {}

  apiData.forEach(({ projectedResults = [], ...item }) => {
    Object.entries(item).forEach(([key, value]) => {
      if (!isIgnored(key)) {
        accumulate(totals, key, value)
      }
    })

    projectedResults.forEach((projItem) => {
      const rate = projItem.chargeRate
      if (!projectedTotals[rate]) projectedTotals[rate] = {}

      Object.entries(projItem).forEach(([key, value]) => {
        accumulate(projectedTotals[rate], key, value)
      })
    })
  })

  const processedTotals = {
    id: 'total_avg',
    ...Object.fromEntries(
      Object.entries(totals).map(([key, values]) => [key, reduceValues(values, key)]),
    ),
  }

  const processedProjectedTotals = Object.entries(projectedTotals).map(
    ([rate, fields]) => {
      const result = {}
      Object.entries(fields).forEach(([key, values]) => {
        result[key] = key === 'chargeRate' ? rate : reduceValues(values, key)
      })
      return result
    },
  )

  return {
    ...processedTotals,
    projectedResults: processedProjectedTotals,
  }
}

export default {
  name: entityName,
  reducer: (state, action) => {
    if (action.type === PERFORMANCE_RESET_STATE) {
      return defaultState
    }
    if (action.type === PERFORMANCE_DATA_LOADING) {
      return { ...state, data: { ...action.meta, payload: action.payload } }
    }
    if (action.type === PERFORMANCE_DATA_LOADED) {
      return { ...state, data: { ...action.meta, data: action.payload } }
    }
    if (action.type === PERFORMANCE_DATA_FAILED) {
      return { ...state, data: { ...action.meta, error: action.payload } }
    }
    return state || defaultState
  },
  selectPerformanceDataIsLoading: ({ performance }) => {
    const status = path(['data', 'status'], performance)
    return status === 'loading'
  },
  selectPerformanceData: ({ performance }) => path(['data', 'data'], performance),
  selectPerformanceDataParams: ({ performance }) =>
    path(['data', 'params'], performance),
  selectPerformanceDataError: ({ performance }) => path(['data', 'error'], performance),
  doResetPerformanceState:
    () =>
    ({ dispatch }) =>
      dispatch({ type: PERFORMANCE_RESET_STATE }),
  doExportPerformanceData:
    () =>
    async ({ store }) => {
      const { data } = store.selectPerformanceData()
      const { rates, interval, propertyName } = store.selectPerformanceDataParams()

      const processRow = (row) => {
        const values = []
        row.forEach((value) => {
          let result = value === null || value === undefined ? '' : value.toString()
          result = result.replace(/"/g, '""')
          if (result.search(/("|,|\n)/g) >= 0) {
            result = `"${result}"`
          }
          values.push(result)
        })
        return `${values.join(',')}\n`
      }

      const headers = [
        { prop: 'startDate', name: 'Start Date' },
        { prop: 'endDate', name: 'End Date' },
        { prop: 'numberOfEvents', name: 'Events' },
        { prop: 'numberOfEventsCharged', name: 'Events Charged' },
        { prop: 'chargeRate', name: 'Charge Rate' },
        { prop: 'netCharges', name: 'Net Charges' },
        { prop: 'projectedRestCosts', name: 'Rest Costs' },
        { prop: 'netProfit', name: 'Net Profit' },
        ...rates.reduce(
          (acc, rate) => [
            ...acc,
            { rate, prop: 'numberOfEventsCharged', name: `Events Charged (${rate}%)` },
            { rate, prop: 'netCharges', name: `Net Charges (${rate}%)` },
            { rate, prop: 'projectedRestCosts', name: `Rest Costs (${rate}%)` },
            { rate, prop: 'netProfit', name: `Net Profit (${rate}%)` },
          ],
          [],
        ),
      ]

      let csvData = `${headers.map((item) => item.name).join(',')}\n`

      data.forEach((row) => {
        const dataToExport = {}
        headers.forEach(({ prop, rate }) => {
          const headerData = rate
            ? row.projectedResults.find(
                (item) => item.chargeRate === rate.toFixed(1),
              )?.[prop]
            : row[prop]
          dataToExport[rate ? `rate${rate}_${prop}` : prop] = headerData
        })
        csvData += processRow(Object.values(dataToExport))
      })
      downloadFile({
        data: [csvData],
        fileName: `${propertyName}_${interval}_performance_export.csv`,
        fileType: 'text/csv',
      })
    },
  doFetchPerformanceData:
    (payload) =>
    async ({ dispatch, apiFetch }) => {
      const { property, propertyName, interval, ranges, hypotheticalChargeRates } =
        payload
      const metaParams = {
        property,
        propertyName,
        interval,
        rates: hypotheticalChargeRates,
      }

      try {
        dispatch({
          type: PERFORMANCE_DATA_LOADING,
          payload,
          meta: { status: 'loading', params: metaParams },
        })

        const apiResponses = await Promise.all(
          ranges.map((range) => {
            const formattedStart = range[0].toFormat('yyyy-MM-dd')
            const formattedEnd = range[1].toFormat('yyyy-MM-dd')
            return apiFetch(
              `/properties/profit_projection/`,
              {
                start: formattedStart,
                end: formattedEnd,
                properties: [property],
                hypotheticalChargeRates,
              },
              {
                method: 'POST',
                cancelationPrefix: `${entityName}_${formattedStart}_${formattedEnd}`,
              },
            )
          }),
        )

        const processedApiData = apiResponses.reduce(
          (acc, response, index) => [
            ...acc,
            {
              ...response[0],
              id: `${ranges[index][0].ts}_${ranges[index][1].ts}`,
              startDate: ranges[index][0],
              endDate: ranges[index][1],
            },
          ],
          [],
        )

        let result = { data: processedApiData }
        if (processedApiData.length > 1) {
          const totals = calculateTotals(processedApiData)
          result = { ...result, totals }
        }

        await dispatch({
          type: PERFORMANCE_DATA_LOADED,
          payload: result,
          meta: { status: 'succeeded', params: metaParams },
        })
        return result
      } catch (err) {
        if (!isAbortError(err)) {
          dispatch({
            type: PERFORMANCE_DATA_FAILED,
            payload: err,
            meta: { status: 'failed', params: metaParams },
          })
          throw err
        }
        return null
      }
    },
}
